import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import CO2Visualization from '../analytics/CO2Visualization';
import { analyticsData } from '../../services/analyticsData';
import { analyticsService } from '../../services/analyticsService';
import { X, Loader2 } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';



const ProjectSearchModal = ({ isOpen, onClose, onProjectSelect }) => {
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  // Load projects when modal opens
  useEffect(() => {
    if (isOpen && projects.length === 0) {
      loadProjects();
    }
  }, [isOpen, projects.length]);

  // Filter projects based on search input
  useEffect(() => {
    if (searchInput) {
      const filtered = projects.filter(project =>
        project.toLowerCase().includes(searchInput.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchInput, projects]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    setError('');
    try {
      const response = await analyticsService.getProjects();
      if (response.success) {
        setProjects(response.data);
        setFilteredProjects(response.data);
      } else {
        setError('Failed to load projects');
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Error loading projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  if (!isOpen) return null;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput && filteredProjects.includes(searchInput)) {
      handleProjectSelect(searchInput);
    }
  };

  const handleProjectSelect = (projectName) => {
    setIsLoading(true);
    // Simulate loading for a brief moment
    setTimeout(() => {
      setIsLoading(false);
      onProjectSelect(projectName);
      onClose();
      setSearchInput('');
    }, 500);
  };

  const handleProjectClick = (projectName) => {
    setSearchInput(projectName);
    handleProjectSelect(projectName);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        
        <h2 className="text-xl mb-4">Projekt laden</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSearch} className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full p-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Projekt suchen"
              autoFocus
              disabled={loadingProjects}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
              {loadingProjects ? 'Laden...' : `${filteredProjects.length} Ergebnisse`}
            </span>
          </div>

          {loadingProjects && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-green-500" />
            </div>
          )}

          {!loadingProjects && (
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
              {filteredProjects.map((projectName) => (
                <button
                  key={projectName}
                  type="button"
                  onClick={() => handleProjectClick(projectName)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors flex items-center"
                >
                  <div>
                    <span className="font-medium">{projectName}</span>
                  </div>
                </button>
              ))}
              {filteredProjects.length === 0 && (
                <div className="px-4 py-2 text-gray-500">
                  Keine Projekte gefunden
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading || !searchInput.trim()}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Laden...</span>
                </>
              ) : (
                <span>Suchen</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AnalyticsHeader = ({ selectedProject, onProjectSelect, timeframe }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleProjectSelect = (projectName) => {
    onProjectSelect(projectName);
  };

  // Helper function to format date range
  const formatTimeframe = (timeframe) => {
    if (!timeframe || !timeframe.start || !timeframe.end) {
      return "Projektzeitraum: Nicht verfügbar";
    }

    try {
      const startDate = new Date(timeframe.start);
      const endDate = new Date(timeframe.end);

      // Format as DD.MM.YYYY
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString();
        return `${day}.${month}.${year}`;
      };

      return `Projektzeitraum: ${formatDate(startDate)}-${formatDate(endDate)}`;
    } catch (error) {
      console.error('Error formatting timeframe:', error);
      return "Projektzeitraum: Nicht verfügbar";
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl">
            Auswertung Projekt{" "}
            <button
              onClick={() => setIsModalOpen(true)}
              className="font-semibold text-green-500 hover:text-green-600 transition-colors focus:outline-none"
            >
              {selectedProject.name}
            </button>
          </h1>
          <p className="text-gray-500">{formatTimeframe(timeframe)}</p>
        </div>
        <div className="flex items-center space-x-4">
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
           
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
     
          </button>
          <div className="flex items-center space-x-2">
            <img 
              src="/logos/UserLogo.png"
              alt="Profile" 
              className="h-8 w-8 rounded-full"
            />
            <span className="text-gray-700 font-medium">Autobahn GmbH</span>
          </div>
        </div>
      </div>

      <ProjectSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProjectSelect={handleProjectSelect}
      />
    </>
  );
};




const KeyMetrics = ({ metrics, co2Data, layerAreas }) => {
  // Helper function to format numbers with German formatting
  const formatGermanNumber = (value) => {
    // Extract number from string like "1,247.5 t" or "462 t CO2e"
    const match = value.match(/[\d,]+\.?\d*/);
    if (!match) return value;

    const numberStr = match[0];
    // Convert to actual number (handle both comma and period as decimal separator)
    const number = parseFloat(numberStr.replace(/,/g, ''));

    if (isNaN(number)) return value;

    // Format with German locale and add unit back
    const unit = value.replace(numberStr, '').trim();
    const formattedNumber = number.toLocaleString('de-DE');

    // Handle CO₂ formatting
    const formattedUnit = unit.replace(/CO2/g, 'CO₂');

    return `${formattedNumber} ${formattedUnit}`.trim();
  };

  // Helper function to render layer areas breakdown
  const renderLayerAreasBreakdown = () => {
    if (!layerAreas) {
      // Show placeholder when no layer areas data is available
      return (
        <div className="text-xs mt-2 text-gray-400 italic">
          Keine Flächenangaben vorhanden
        </div>
      );
    }

    const formatArea = (value) => {
      if (value === undefined || value === null || value === 0) return '0';
      return value.toLocaleString('de-DE');
    };

    return (
      <div className="text-sm mt-2 text-black font-medium">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">DS</div>
          <div className="text-center">BS</div>
          <div className="text-center">TS</div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center whitespace-nowrap">{formatArea((layerAreas.deckschichten || []).reduce((s, e) => s + (e.flaeche || 0), 0))} m²</div>
          <div className="text-center whitespace-nowrap">{formatArea((layerAreas.binderschichten || []).reduce((s, e) => s + (e.flaeche || 0), 0))} m²</div>
          <div className="text-center whitespace-nowrap">{formatArea((layerAreas.tragschichten || []).reduce((m, e) => Math.max(m, e.flaeche || 0), 0))} m²</div>
        </div>
      </div>
    );
  };

  // Calculate 1990 CO₂ savings
  const calculate1990Savings = () => {
    if (!co2Data || !co2Data.summary) {
      console.log('No co2Data or summary available');
      return "Keine Daten verfügbar";
    }

    const currentTotal = co2Data.summary.totalEmissions;
    const total1990 = co2Data.summary.totalEmissions1990;

    console.log('Current Total Emissions:', currentTotal);
    console.log('1990 Total Emissions:', total1990);

    if (!currentTotal || !total1990) {
      console.log('Missing current or 1990 data');
      return "Keine 1990 Daten verfügbar";
    }

    // Calculate total current emissions (A1 + A2 + A3 + A4)
    const currentSum = (currentTotal.A1 || 0) + (currentTotal.A2 || 0) + (currentTotal.A3 || 0) + (currentTotal.A4 || 0);

    // Calculate total 1990 emissions (A1 + A2 + A3 + A4)
    const total1990Sum = (total1990.A1 || 0) + (total1990.A2 || 0) + (total1990.A3 || 0) + (total1990.A4 || 0);

    console.log('Current Sum (A1+A2+A3+A4):', currentSum);
    console.log('1990 Sum (A1+A2+A3+A4):', total1990Sum);

    // Calculate savings: 1990 total - current total (positive = savings, negative = increase)
    const savings = total1990Sum - currentSum;

    console.log('Calculated Savings (1990 - Current):', savings);

    // Format with German number formatting (thousands separator and comma as decimal separator)
    const absValue = Math.abs(savings);
    const formattedSavings = absValue.toLocaleString('de-DE', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });

    console.log('Formatted Savings:', `${savings >= 0 ? '' : '-'}${formattedSavings} t CO₂e`);

    return `${savings >= 0 ? '' : '-'}${formattedSavings} t CO₂e`;
  };

  // Calculate percentage difference between current CO₂e and 1990 savings for Emissionsfaktor trend
  const calculateEmissionsfaktorTrend = () => {
    if (!co2Data || !co2Data.summary) {
      return "0%";
    }

    const currentTotal = co2Data.summary.totalEmissions;
    const total1990 = co2Data.summary.totalEmissions1990;

    if (!currentTotal || !total1990) {
      return "0%";
    }

    // Calculate total current emissions (A1 + A2 + A3 + A4)
    const currentSum = (currentTotal.A1 || 0) + (currentTotal.A2 || 0) + (currentTotal.A3 || 0) + (currentTotal.A4 || 0);

    // Calculate total 1990 emissions (A1 + A2 + A3 + A4)
    const total1990Sum = (total1990.A1 || 0) + (total1990.A2 || 0) + (total1990.A3 || 0) + (total1990.A4 || 0);

    // Calculate percentage difference: ((current - 1990) / 1990) * 100
    const percentageDifference = total1990Sum > 0 ? ((currentSum - total1990Sum) / total1990Sum) * 100 : 0;

    // Format as percentage with 1 decimal place and German formatting
    const formattedPercentage = Math.abs(percentageDifference).toFixed(1).replace('.', ',');

    return `${percentageDifference >= 0 ? '+' : '-'}${formattedPercentage}%`;
  };

  // Calculate equivalent asphalt tonnes for savings
  const calculateSavingsEquivalent = () => {
    if (!co2Data || !co2Data.summary) {
      return "";
    }

    const currentTotal = co2Data.summary.totalEmissions;
    const total1990 = co2Data.summary.totalEmissions1990;

    if (!currentTotal || !total1990) {
      return "";
    }

    // Calculate savings in tonnes
    const currentSum = (currentTotal.A1 || 0) + (currentTotal.A2 || 0) + (currentTotal.A3 || 0) + (currentTotal.A4 || 0);
    const total1990Sum = (total1990.A1 || 0) + (total1990.A2 || 0) + (total1990.A3 || 0) + (total1990.A4 || 0);
    const savingsInTonnes = total1990Sum - currentSum;

    // Find Emissionsfaktor value from metrics
    const emissionsfaktorMetric = metrics.find(m => m.name === "Emissionsfaktor");
    if (!emissionsfaktorMetric) {
      return "";
    }

    // Extract numeric value from "42,6 kg CO2e/t" format
    const emissionsfaktorMatch = emissionsfaktorMetric.value.match(/[\d,]+\.?\d*/);
    if (!emissionsfaktorMatch) {
      return "";
    }

    const emissionsfaktorValue = parseFloat(emissionsfaktorMatch[0].replace(',', '.'));

    if (emissionsfaktorValue === 0 || savingsInTonnes <= 0) {
      return "";
    }

    // Calculate equivalent asphalt tonnes: savings (t CO₂e) / emission factor (kg CO₂e/t) * 1000
    const equivalentTonnes = (savingsInTonnes * 1000) / emissionsfaktorValue;

    // Format with German number formatting
    const formattedTonnes = equivalentTonnes.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `Entsprechen ${formattedTonnes} Tonnen Asphaltmischgut`;
  };

  // Create the savings metric
  const savingsMetric = {
    name: "Einsparungen gegenüber 1990",
    value: calculate1990Savings(),
    trend: calculateSavingsEquivalent() // Use the equivalent calculation as trend
  };

  // Reorder metrics: swap CO₂e Emissionen and Emissionsfaktor positions, and update Emissionsfaktor trend
  const reorderedMetrics = metrics.map(metric => {
    if (metric.name === "Emissionsfaktor") {
      return {
        ...metric,
        trend: calculateEmissionsfaktorTrend() // Use calculated percentage difference
      };
    }
    return metric;
  });

  // Swap positions: find indices and swap
  const co2Index = reorderedMetrics.findIndex(m => m.name === "CO₂e Emissionen");
  const emissionIndex = reorderedMetrics.findIndex(m => m.name === "Emissionsfaktor");

  if (co2Index !== -1 && emissionIndex !== -1) {
    [reorderedMetrics[co2Index], reorderedMetrics[emissionIndex]] = [reorderedMetrics[emissionIndex], reorderedMetrics[co2Index]];
  }

  // Combine reordered metrics with savings metric
  const allMetrics = [...reorderedMetrics, savingsMetric];

  return (
    <div className="grid grid-cols-4 gap-4">
      {allMetrics.map((metric, index) => (
        <Card key={index}>
          <div className="p-6">
            <div className="text-sm text-gray-500">{metric.name}</div>
            <div className="text-2xl font-semibold mt-2">
              <span>{metric.name === "Einsparungen gegenüber 1990" ? metric.value : formatGermanNumber(metric.value)}</span>
            </div>
            {metric.name === "Menge Asphaltmischgut" ? (
              // Show layer areas breakdown for Asphaltmischgut
              renderLayerAreasBreakdown()
            ) : metric.name === "CO₂e Emissionen" ? (
              // Comment out the percentage comparison for CO₂e Emissionen
              <div className="text-sm mt-2 invisible">
                {/* Placeholder to maintain spacing */}
                &nbsp;
              </div>
            ) : metric.name === "Einsparungen gegenüber 1990" ? (
              // Show equivalent asphalt tonnes for savings
              <div className="text-sm mt-2 text-green-500">
                {metric.trend}
              </div>
            ) : metric.name === "Emissionsfaktor" ? (
              // Show calculated percentage difference for Emissionsfaktor
              <div className={`text-sm mt-2 ${metric.trend.startsWith('+') ? 'text-red-500' : 'text-green-500'}`}>
                {metric.trend} vs. 1990
              </div>
            ) : (
              <div className={`text-sm mt-2 ${metric.trend.startsWith('+') ? 'text-red-500' : 'text-green-500'}`}>
                {metric.trend} vs. Referenzwert
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};
export const Analytics = () => {
  const { isExpanded } = useSidebar();
  const [selectedProject, setSelectedProject] = useState({
    name: null, // Will be set to most recent project
  });
  const [keyMetrics, setKeyMetrics] = useState(analyticsData.keyMetrics); // Start with mock data
  const [co2Data, setCo2Data] = useState(null); // Start with null to prevent legacy component rendering
  const [timeframe, setTimeframe] = useState(null); // Project timeframe data
  const [layerAreas, setLayerAreas] = useState(null); // Construction layer areas data
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true); // Start loading immediately
  const [isLoadingCO2, setIsLoadingCO2] = useState(true); // Start loading immediately
  const [isLoadingTimeframe, setIsLoadingTimeframe] = useState(false);
  const [isLoadingLayerAreas, setIsLoadingLayerAreas] = useState(false);
  const [error, setError] = useState('');
  const [showCombinedView, setShowCombinedView] = useState(false); // Toggle for 1990 comparison

  // Load projects and set the default one
  useEffect(() => {
    const DEFAULT_PROJECT = 'A 6 FBE AK Walldorf - AS Wiesloch Rauenberg FR MA CO2 Maßnahme';

    const loadDefaultProject = async () => {
      try {
        const response = await analyticsService.getProjects();
        if (response.success && response.data.length > 0) {
          // Prefer the configured default project if it exists in the list
          const defaultProject = response.data.includes(DEFAULT_PROJECT)
            ? DEFAULT_PROJECT
            : response.data[0];
          setSelectedProject({ name: defaultProject });
        } else {
          setSelectedProject({ name: DEFAULT_PROJECT });
        }
      } catch (error) {
        console.error('Error loading default project:', error);
        setSelectedProject({ name: DEFAULT_PROJECT });
      }
    };

    loadDefaultProject();
  }, []);

  // Fetch analytics data when project changes
  useEffect(() => {
    if (selectedProject.name) {
      fetchAnalyticsData(selectedProject.name);
    }
  }, [selectedProject.name]);

  const fetchAnalyticsData = async (projectName) => {
    setIsLoadingMetrics(true);
    setIsLoadingCO2(true);
    setIsLoadingTimeframe(true);
    setIsLoadingLayerAreas(true);
    setError('');

    // Hardcoded timeframe overrides for specific projects
    const TIMEFRAME_OVERRIDES = {
      'A 6 FBE AK Walldorf - AS Wiesloch Rauenberg FR MA CO2 Maßnahme': {
        start: '2025-06-12',
        end: '2025-08-22',
      },
    };

    try {
      // Fetch key metrics
      const metricsResponse = await analyticsService.getKeyMetrics(projectName);
      if (metricsResponse.success) {
        setKeyMetrics(metricsResponse.data);
      }

      // Fetch CO2 breakdown data
      const co2Response = await analyticsService.getCO2Breakdown(projectName);
      if (co2Response.success) {
        setCo2Data(co2Response.data);
      }

      // Fetch timeframe data (overridden for specific projects)
      if (TIMEFRAME_OVERRIDES[projectName]) {
        setTimeframe(TIMEFRAME_OVERRIDES[projectName]);
      } else {
        const timeframeResponse = await analyticsService.getProjectTimeframe(projectName);
        if (timeframeResponse.success) {
          setTimeframe(timeframeResponse.data);
        }
      }

      // Fetch layer areas data
      const layerAreasResponse = await analyticsService.getLayerAreas(projectName);
      if (layerAreasResponse.success) {
        setLayerAreas(layerAreasResponse.data);
      } else {
        setLayerAreas(null); // No layer areas data available
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data');
      // Keep using mock data on error
    } finally {
      setIsLoadingMetrics(false);
      setIsLoadingCO2(false);
      setIsLoadingTimeframe(false);
      setIsLoadingLayerAreas(false);
    }
  };

  const handleProjectSelect = (projectName) => {
    setSelectedProject({
      name: projectName,
    });
  };

  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  return (
    <div className={`${leftMargin} h-screen bg-gray-50 flex flex-col transition-all duration-300`}>
      <div className="p-8 flex-shrink-0">
        <AnalyticsHeader
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
          timeframe={timeframe}
        />

        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="mt-8">
          {isLoadingMetrics ? (
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, index) => (
                <Card key={index}>
                  <div className="p-6 flex justify-center items-center" style={{ height: '120px' }}>
                    <Loader2 className="h-6 w-6 animate-spin text-green-500" />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <KeyMetrics metrics={keyMetrics} co2Data={co2Data} layerAreas={layerAreas} />
          )}
        </div>
      </div>

      <div className="flex-1 px-8 pb-8 min-h-0">
        {isLoadingCO2 ? (
          <Card className="h-full">
            <div className="p-6 flex justify-center items-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-green-500" />
            </div>
          </Card>
        ) : (
          <CO2Visualization
            data={co2Data}
            fullHeight={true}
            title={showCombinedView ? "CO₂e Vergleich (1990 vs. Aktuell)" : "Aktuelle CO₂e Emissionen"}
            showCombinedData={showCombinedView}
            showToggleButton={true}
            onToggleView={() => setShowCombinedView(!showCombinedView)}
            isToggleActive={showCombinedView}
          />
        )}
      </div>
    </div>
  );
};

export default Analytics;